import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDate, getGabja, isEmbedMode, requestParentResize } from "../../util";
import ThreeLuck from "../../components/ThreeLuck";
import ThreeLuckEmbed from "../../components/ThreeLuckEmbed";
import ResultImage from "../../components/ResultImage";
import { isIOS } from "react-device-detect";
import html2canvas from "html2canvas";
import { useGPTStore } from "../../store/useGPT";
import { useFortuneLabStore } from "../../store/useFortuneLab";

const colorLabelMap: Record<string, string> = {
  red: "빨간색",
  orange: "주황색",
  yellow: "노란색",
  green: "초록색",
  blue: "파란색",
  navy: "남색",
  purple: "보라색",
  white: "흰색",
  black: "검은색",
};

const FortuneLabResultPage = () => {
  useEffect(() => {
    document.title = "오늘의 운세";
  }, []);

  useEffect(() => {
    if (typeof (window as any).fbq === "function") {
      (window as any).fbq("track", "CompleteRegistration");
    }
  }, []);

  useEffect(() => {
    const updateMainStyle = () => {
      const mainElement = document.querySelector(".main_container main");
      if (mainElement instanceof HTMLElement) {
        const additionalPadding = isIOS ? 120 : 0;
        mainElement.style.paddingBottom = `${additionalPadding}px`;
      }
    };

    updateMainStyle();
    window.addEventListener("resize", updateMainStyle);
    return () => window.removeEventListener("resize", updateMainStyle);
  }, []);

  const navigate = useNavigate();

  const { registeredUser, postData, reset } = useFortuneLabStore();
  const { getSajuLoading, result, getSaju } = useGPTStore();

  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const embedMode = isEmbedMode();

  useEffect(() => {
    if (!embedMode) {
      return;
    }
    requestParentResize([0, 400, 800]);
  }, [embedMode]);

  useEffect(() => {
    if (!registeredUser || !postData) {
      navigate("/fortune-lab");
      return;
    }

    const interval = setInterval(() => {
      setLoadingProgress((prev) => (prev < 90 ? prev + 15 : prev));
    }, 1500);

    getSaju({
      id: registeredUser.id,
      birthDayJu: registeredUser.birthDayJu,
      someDayJu: getGabja(0),
      trkId: registeredUser.trkId ?? "",
      path: "/fortune-lab/saju/getLucky",
    });

    return () => clearInterval(interval);
  }, [registeredUser, postData, getSaju, navigate]);

  useEffect(() => {
    if (!getSajuLoading) {
      setLoadingProgress(100);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [getSajuLoading]);

  useEffect(() => {
    if (isLoading || !embedMode) {
      return;
    }
    requestParentResize([0, 400, 800, 1200]);
  }, [embedMode, isLoading, result]);

  const mainRef = useRef<HTMLElement>(null);
  const todayLuckRef = useRef<HTMLElement>(null);
  const saveResultImage = () => {
    if (!mainRef.current || !todayLuckRef.current) return;

    const mainElement = mainRef.current;
    const todayLuckElement = todayLuckRef.current;

    const previousHeight = mainElement.style.height;
    const previousOverflow = mainElement.style.overflow;
    const previousScrollTop = mainElement.scrollTop;

    mainElement.style.height = `${mainElement.scrollHeight}px`;
    mainElement.style.overflow = "visible";
    mainElement.scrollTop = 0;

    todayLuckElement.classList.add("screenshot");

    html2canvas(mainElement, {
      ignoreElements: (element) =>
        element.classList.contains("banner_wrapper") ||
        element.classList.contains("kakao_button") ||
        element.classList.contains("gradient-button") ||
        element.tagName === "FOOTER",
      useCORS: true,
    })
      .then((canvas) => {
        const imageURL = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imageURL;
        link.download = "운세_결과.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("이미지 저장 실패:", error);
      })
      .finally(() => {
        todayLuckElement.classList.remove("screenshot");
        mainElement.style.height = previousHeight;
        mainElement.style.overflow = previousOverflow;
        mainElement.scrollTop = previousScrollTop;
      });
  };

  const onClickGoFirst = useCallback(() => {
    reset();
    navigate("/fortune-lab");
  }, [navigate, reset]);

  const colorClass = result?.color || "black";
  const colorLabel =
    (result?.color && colorLabelMap[result.color]) || "-";
  const fortuneRows = [
    { key: "color", label: "색상", value: colorLabel },
    { key: "gift", label: "물건", value: result?.giftKey?.trim() || "-" },
    { key: "food", label: "음식", value: result?.foodKey?.trim() || "-" },
    {
      key: "number",
      label: "숫자",
      value:
        result?.number !== undefined && result?.number !== null
          ? String(result.number)
          : "-",
    },
  ];

  if ((!registeredUser || !postData) && !isLoading) {
    return (
      <div id="result_page" className="main_container">
        <div className="result_loading">
          <div className="legend_backdrop">
            <div className="many_people_img"></div>
            <div className="many_people_text">지금은 접속자가 많아요 😭</div>
            <div className="many_people_small_text">
              잠시 후에 다시 시도해주세요!
            </div>
            <div className="many_people_small_text">
              죄송한 마음을 담아 꽃다발을 선물합니다 🌸
            </div>
            <button
              style={{ marginBottom: 0, marginTop: 24 }}
              onClick={onClickGoFirst}
              className="gradient-button"
            >
              처음으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="result_page" className="main_container">
      <main ref={mainRef}>
        <div className="short_date">{getDate(0).short}</div>
        <div className="name_luck_result">
          <span className="user_name">
            {result?.name || registeredUser?.name || "선생"}
          </span>
          님의{" "}
          <span
            ref={todayLuckRef}
            className={`name_luck_result_text ${result?.color || "black"}`}
          >
            오늘의 운세
          </span>
        </div>

        <ResultImage imageKey={result?.imageKey || "갑자"} />

        <div className="all_luck_wrapper" style={{ marginTop: 60 }}>
          <div className="all_luck_title" style={{ marginBottom: 28 }}>
            <span className={`today_color ${colorClass}`}>오늘의 행운</span>을
            확인하세요!
          </div>
          {fortuneRows.map((row) => (
            <div className={`today_luck_row ${row.key}`} key={row.key}>
              <div className="left">
                <span className="icon"></span>
                행운의 <span className="today_luck_bold">{row.label}</span>
              </div>
              <div className="right">
                <span className={`today_color ${colorClass}`}>{row.value}</span>
              </div>
            </div>
          ))}
        </div>

        {(embedMode ? (
          <ThreeLuckEmbed
            money_score={result?.moneyScore || 60}
            money_title={result?.moneyTitle || "재물운"}
            money_sub_title={result?.moneySubTitle || "재물운"}
            money_content={
              result?.moneyContext ||
              "소소한 재물이 생기겠지만 큰 기대는 하지 않는 것이 좋습니다."
            }
            love_score={result?.loveScore || 75}
            love_title={result?.loveTitle || "연애운"}
            love_sub_title={result?.loveSubTitle || "연애운"}
            love_content={
              result?.loveContext ||
              "우연히 만나게 될 사람과의 대화가 기대되는 하루입니다."
            }
            job_score={result?.jobScore || 65}
            job_title={result?.jobTitle || "직장운"}
            job_sub_title={result?.jobSubTitle || "직장운"}
            job_content={
              result?.jobContext ||
              "작은 프로젝트에 주력하는 것이 효과적일 것입니다."
            }
            showAds={false}
          />
        ) : (
          <ThreeLuck
            money_score={result?.moneyScore || 60}
            money_title={result?.moneyTitle || "재물운"}
            money_sub_title={result?.moneySubTitle || "재물운"}
            money_content={
              result?.moneyContext ||
              "소소한 재물이 생기겠지만 큰 기대는 하지 않는 것이 좋습니다."
            }
            love_score={result?.loveScore || 75}
            love_title={result?.loveTitle || "연애운"}
            love_sub_title={result?.loveSubTitle || "연애운"}
            love_content={
              result?.loveContext ||
              "우연히 만나게 될 사람과의 대화가 기대되는 하루입니다."
            }
            job_score={result?.jobScore || 65}
            job_title={result?.jobTitle || "직장운"}
            job_sub_title={result?.jobSubTitle || "직장운"}
            job_content={
              result?.jobContext ||
              "작은 프로젝트에 주력하는 것이 효과적일 것입니다."
            }
            showAds={false}
          />
        ))}

        <button
          onClick={saveResultImage}
          className="gradient-button"
          style={{ marginTop: 32, marginBottom: 60 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M448 80c8.8 0 16 7.2 16 16l0 319.8-5-6.5-136-176c-4.5-5.9-11.6-9.3-19-9.3s-14.4 3.4-19 9.3L202 340.7l-30.5-42.7C167 291.7 159.8 288 152 288s-15 3.7-19.5 10.1l-80 112L48 416.3l0-.3L48 96c0-8.8 7.2-16 16-16l384 0zM64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32zm80 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z" />
          </svg>
          이미지로 저장하기
        </button>

        {isLoading && (
          <div className="result_loading">
            <div className="legend_backdrop">
              <div className="leaf_img"></div>
              <div className="loading_text">운세를 가져오고 있어요 😊</div>
              <div className="loading_bar_container">
                <div
                  className="loading_bar"
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FortuneLabResultPage;
